import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ENTWAESSERUNG_SCHWELLEN } from '../evaluators/entwaesserung';

type Stufe = 'I' | 'II' | 'III';
/** Ein Fall einer Regel: Bedingung (fehlt bei Regeln mit nur einem Ausgang) und Ergebnis. */
type Fall = { wenn?: string; ergebnis: Stufe };
type Regel = { nr: number; titel: string; text: string; faelle: Fall[] };

/**
 * Hilfe am (i) des Feldes "Entwässerungsverhalten".
 *
 * Vorher stand hier ein einziger Textblock; die Regeln waren darin nur an
 * Zeilenumbrüchen zu erkennen. Die Zahlen kommen aus denselben Konstanten,
 * mit denen der Vorschlag gerechnet wird (ENTWAESSERUNG_SCHWELLEN) - eine
 * abgeschriebene Zahl hier würde sonst irgendwann von der Rechnung abweichen,
 * ohne dass es jemandem auffällt.
 */
@Component({
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Entwässerungsverhalten</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">

			<div class="gruppen">
				<div class="gruppe" *ngFor="let g of gruppen" [ngClass]="'stufe-' + g.stufe">
					<div class="ziffer">{{ g.stufe }}</div>
					<div class="was">{{ g.was }}</div>
				</div>
			</div>

			<section>
				<h6>Woher der Wert kommt</h6>
				<p>
					Der Wert wird aus der Substratfolge vorgeschlagen und zieht bei jeder Änderung nach.
					Sobald Sie ihn von Hand ändern, bleibt er stehen: die Automatik fasst ihn nicht mehr an.
					Zum Zurücksetzen das Feld leeren.
				</p>
			</section>

			<section>
				<h6>Was bewertet wird</h6>
				<ul class="knapp">
					<li>Das Substratprofil von 0 bis {{ schwellen.fensterCm }} cm.</li>
					<li>
						Endet die Bohrung früher, zählt die letzte Schicht bis {{ schwellen.fensterCm }} cm
						weiter (wie bei nWSK).
					</li>
					<li>Torf, Humus und Mudden bleiben unberücksichtigt.</li>
					<li>Moorpunkte bekommen immer <span class="stufe-inline stufe-II">II</span>.</li>
				</ul>
			</section>

			<section>
				<h6>Regeln <span class="beiwort">· es gewinnt die schwerste zutreffende</span></h6>
				<table class="table table-sm regel-tabelle">
					<tbody>
						<tr *ngFor="let r of regeln">
							<td class="nr">{{ r.nr }}</td>
							<td>
								<div class="titel">{{ r.titel }}</div>
								<div class="text">{{ r.text }}</div>
								<ng-container *ngIf="r.faelle.length > 1">
									<div class="fall" *ngFor="let f of r.faelle">
										<span class="wenn">{{ f.wenn }}</span>
										<span class="pfeil">&rarr;</span>
										<span class="stufe-inline" [ngClass]="'stufe-' + f.ergebnis">{{ f.ergebnis }}</span>
									</div>
								</ng-container>
							</td>
							<td class="ergebnis">
								<span
									*ngIf="r.faelle.length === 1"
									class="stufe-inline"
									[ngClass]="'stufe-' + r.faelle[0].ergebnis"
								>{{ r.faelle[0].ergebnis }}</span>
							</td>
						</tr>
					</tbody>
				</table>

				<div class="hinweis">
					<div>
						<strong>„Am Stück“</strong> heißt: direkt aufeinanderfolgende Zeilen werden
						zusammengezählt (30 cm L über 30 cm T = 60 cm). Eine Sand- oder Torflage
						dazwischen trennt.
					</div>
					<div class="mt-2">
						Die <strong>Dicken-Grenzen</strong> zählen ab dem genannten Wert: genau
						{{ schwellen.kompaktCm }}, {{ schwellen.summeAnlehmigCm }} bzw.
						{{ schwellen.summeBaenderCm }} cm lösen schon aus.
					</div>
				</div>
			</section>

			<p class="quelle">
				Grundlage ist die Anlage 2. Statt der dort beschriebenen Gewichtung nach
				Mächtigkeitsanteilen rechnet die App mit den oben genannten Schwellen in cm.
			</p>
		</div>
	`,
	styles: [`
		.modal-body {
			font-size: 0.9rem;
		}
		section {
			margin-top: 1.1rem;
		}
		h6 {
			font-weight: 600;
			margin-bottom: 0.4rem;
		}
		.beiwort {
			font-weight: 400;
			font-size: 0.85rem;
			color: #6c757d;
		}
		p {
			margin-bottom: 0;
		}

		/* Die drei Gruppen als Kopfzeile: Ziffer gross, Kurzbeschreibung daneben */
		.gruppen {
			display: flex;
			flex-wrap: wrap;
			gap: 0.5rem;
		}
		.gruppe {
			flex: 1 1 10rem;
			display: flex;
			align-items: center;
			gap: 0.6rem;
			padding: 0.5rem 0.7rem;
			border: 1px solid;
			border-radius: 0.35rem;
		}
		.gruppe .ziffer {
			font-size: 1.3rem;
			font-weight: 700;
			line-height: 1;
			min-width: 2rem;
			text-align: center;
		}
		.gruppe .was {
			font-size: 0.8rem;
			line-height: 1.25;
		}

		/* Sand hell, Lehm/Ton dunkler: die Farbe folgt der Schwere des Substrats */
		.stufe-I {
			background: #f3f7ef;
			border-color: #c9ddbc;
			color: #3f6b3a;
		}
		.stufe-II {
			background: #fdf6e3;
			border-color: #e8d7a8;
			color: #8a6316;
		}
		.stufe-III {
			background: #f7ebe6;
			border-color: #e0c3b6;
			color: #8a4b38;
		}
		.stufe-inline {
			display: inline-block;
			min-width: 2.1rem;
			padding: 0.05rem 0.35rem;
			border: 1px solid;
			border-radius: 0.25rem;
			font-weight: 700;
			font-size: 0.8rem;
			text-align: center;
		}

		ul.knapp {
			margin-bottom: 0;
			padding-left: 1.1rem;
		}
		ul.knapp li {
			margin-bottom: 0.15rem;
		}

		.regel-tabelle {
			margin-bottom: 0.6rem;
		}
		.regel-tabelle td {
			vertical-align: top;
			padding: 0.4rem 0.5rem;
		}
		.regel-tabelle .nr {
			width: 1.6rem;
			color: #6c757d;
			font-variant-numeric: tabular-nums;
		}
		.regel-tabelle .ergebnis {
			width: 3rem;
			text-align: right;
		}
		.regel-tabelle .titel {
			font-weight: 600;
		}
		.regel-tabelle .text {
			color: #495057;
		}
		.regel-tabelle .fall {
			display: flex;
			align-items: center;
			gap: 0.4rem;
			margin-top: 0.3rem;
		}
		.regel-tabelle .wenn {
			color: #495057;
		}
		.regel-tabelle .pfeil {
			color: #adb5bd;
		}

		.hinweis {
			font-size: 0.85rem;
			padding: 0.5rem 0.75rem;
			background: #f8f9fa;
			border-radius: 0.25rem;
		}

		.quelle {
			margin-top: 1.1rem;
			font-size: 0.8rem;
			color: #6c757d;
		}
	`]
})
export class EntwaesserungInfoModalComponent {
	activeModal = inject(NgbActiveModal);

	/** Die Schwellen der Rechnung, damit die Hilfe keine abgeschriebenen Zahlen nennt. */
	schwellen = ENTWAESSERUNG_SCHWELLEN;

	/** Anlage 2: was die drei Gruppen bedeuten. */
	gruppen: { stufe: Stufe; was: string }[] = [
		{ stufe: 'I', was: 'reinsandige Substrate' },
		{ stufe: 'II', was: 'anlehmige und lehmige Sande, Humus, tonarme Schluffe' },
		{ stufe: 'III', was: 'Tone und Lehme' },
	];

	regeln: Regel[] = [
		{
			nr: 1,
			titel: 'Schwere Schicht am Stück',
			text: `Lehm, Ton, Schluff oder Kalk (L, T, U, K) ab ${ENTWAESSERUNG_SCHWELLEN.kompaktCm} cm am Stück`,
			faelle: [
				{ wenn: `beginnt oberhalb ${ENTWAESSERUNG_SCHWELLEN.einsatztiefeCm} cm Tiefe`, ergebnis: 'III' },
				{ wenn: `beginnt tiefer als ${ENTWAESSERUNG_SCHWELLEN.einsatztiefeCm} cm`, ergebnis: 'II' },
			],
		},
		{
			nr: 2,
			titel: 'Anlehmiges in Summe',
			text: `Anlehmige, lehmige und schluffige Schichten (al…, l…, u…) zusammen ab ${ENTWAESSERUNG_SCHWELLEN.summeAnlehmigCm} cm`,
			faelle: [{ ergebnis: 'II' }],
		},
		{
			nr: 3,
			titel: 'Bändersand',
			text: `Lehmige/schluffige Lagen und Staubsand (l…, u…, stbS sowie L, T, U, K) zusammen ab ${ENTWAESSERUNG_SCHWELLEN.summeBaenderCm} cm`,
			faelle: [{ ergebnis: 'II' }],
		},
		{
			nr: 4,
			titel: 'Tiefe Unterlagerung',
			text: `Unter sonst reinem Sand liegt unterhalb ${ENTWAESSERUNG_SCHWELLEN.fensterCm} cm ab ${ENTWAESSERUNG_SCHWELLEN.kompaktCm} cm am Stück Lehm, Ton, Schluff, Kalk oder Staubsand`,
			faelle: [{ ergebnis: 'II' }],
		},
		{
			nr: 5,
			titel: 'Sonst',
			text: 'reine Sande: gS, mS, mfS, fS',
			faelle: [{ ergebnis: 'I' }],
		},
	];
}
